// src/pages/PrimerIngreso.js
// El formulario del primer ingreso (programa de Bienvenida, docs/planes/CURSOS_PLAN.md §0.1 #46): quien entra con una
// contraseña que le dieron (la de su carta de bienvenida, o la de "Nueva Pass" de un administrador) crea la suya antes de
// ver el sistema, y la que le dieron deja de servir.
// Lo muestra Login.js justo después de entrar (vive dentro de la caja del login: usa su CSS, Login.css, con su paleta
// fija). La regla de la contraseña: utils/contrasenaPropia.js (espejo de la del servidor, functions/bienvenida/reglas.js).
import React, { useState } from 'react';
import { errorContrasenaPropia, requisitosContrasena } from '../utils/contrasenaPropia';
import { KeyIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, FloppyDiskIcon } from '../components/ui/Icons';

const estiloBoton = (loading) => ({
  width: '100%',
  padding: '12px',
  background: 'var(--color-primario)', // sólido: cero degradados nuevos (ESTETICA §2), aunque el botón viejo del Login lo tenga
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: loading ? 'not-allowed' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'transform 0.2s',
  opacity: loading ? 0.7 : 1
});

// Por qué tiene que crearla (lo marca el servidor en `usuarios/{uid}.cambioPasswordMotivo`).
const INTRO = {
  carta: 'Es tu primer ingreso. La contraseña de tu carta de bienvenida deja de servir: creá una que solo vos sepás y no se la digás a nadie.',
  nueva_pass: 'Entraste con una contraseña que te dio un administrador y deja de servir ahora: creá una que solo vos sepás y no se la digás a nadie.',
};

/**
 * @param {{ email: string, contrasenaCarta: string, motivo?: 'carta'|'nueva_pass', loading: boolean, error: string,
 *           onGuardar: (nueva: string) => void, onVolver: () => void }} props
 *   contrasenaCarta: la que acaba de escribir para entrar (no puede repetirla).
 */
function PrimerIngreso({ email, contrasenaCarta, motivo = 'carta', loading, error, onGuardar, onVolver }) {
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [verla, setVerla] = useState(false);
  const [errorLocal, setErrorLocal] = useState('');
  const opciones = { correo: email, contrasenaCarta };

  const guardar = (e) => {
    e.preventDefault();
    const problema = errorContrasenaPropia(nueva, repetida, opciones);
    setErrorLocal(problema || '');
    if (!problema) onGuardar(nueva);
  };

  const mensaje = errorLocal || error;

  return (
    <form onSubmit={guardar}>
      <div className="primer-ingreso-intro">
        <h3><KeyIcon className="w-5 h-5" /> Creá tu contraseña</h3>
        <p>{INTRO[motivo] || INTRO.carta}</p>
      </div>

      <div className="form-group">
        <label>Contraseña nueva:</label>
        <div style={{ position: 'relative' }}>
          <input
            type={verla ? 'text' : 'password'}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            required
            autoComplete="new-password"
            autoFocus
            disabled={loading}
            style={{ paddingRight: '44px' }}
          />
          <button
            type="button"
            onClick={() => setVerla(!verla)}
            disabled={loading}
            aria-label={verla ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            title={verla ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            style={{
              position: 'absolute',
              top: '50%',
              right: '10px',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              padding: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#888',
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            {verla ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Repetila:</label>
        <input
          type={verla ? 'text' : 'password'}
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
          required
          autoComplete="new-password"
          disabled={loading}
        />
      </div>

      <ul className="primer-ingreso-requisitos">
        {requisitosContrasena(nueva, opciones).map((r) => (
          <li key={r.id} className={r.ok ? 'cumplido' : ''}>
            <CheckCircleIcon className="w-4 h-4" /> {r.texto}
          </li>
        ))}
      </ul>

      {mensaje && <div className="error-message">{mensaje}</div>}

      <button type="submit" disabled={loading} style={estiloBoton(loading)}>
        {loading
          ? <><ArrowPathIcon className="w-4 h-4" /> Guardando...</>
          : <><FloppyDiskIcon className="w-4 h-4" /> Guardar y entrar</>}
      </button>

      <div className="login-footer">
        <button type="button" onClick={onVolver} disabled={loading} className="btn-link">
          Volver al inicio de sesión
        </button>
      </div>
    </form>
  );
}

export default PrimerIngreso;
